/**
 * Training (designs/Caged Training, Training States and the Desktop twins; game-design §5):
 * spend skill points on the 10 skills. Taps are pending until Confirm; Undo steps back one
 * change, Cancel drops them all. Suggested builds spend all points in one step.
 */
import {
  applyPreset,
  availablePoints,
  buildLabel,
  MAX_RANK,
  rankCost,
  resetCost,
  SKILL_GROUPS,
  SKILL_IDS,
  SKILLS,
  spentPoints,
  type PerkId,
  type PresetId,
  type SkillDef,
  type SkillGroup,
  type SkillId,
  type SkillRanks,
} from '@caged/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { SpBar, XpBar } from '../components/Bars';
import { DesktopHeader, TopBar } from '../components/Chrome';
import { FighterArt } from '../components/FighterArt';
import { Modal } from '../components/Modal';
import { LevelBadge, SkillPointLine } from '../components/ProgressWidgets';
import ui from '../components/ui.module.css';
import { formatNumber } from '../format';
import { useLayoutMode, useMyFighter, useMyLook, useScrollTop } from '../hooks';
import { levelView, perkParams, skillBarView, skillEffect, skillsInGroup } from '../progression';
import '../ui.css';
import s from './TrainingScreen.module.css';

const PRESET_IDS: PresetId[] = ['striker', 'wrestler', 'tank', 'balanced'];
const PERK_RANKS = [5, 10] as const;
const TOAST_MS = 4000;

interface Toast {
  skill: SkillId;
  rank: 5 | 10;
}

function applySteps(ranks: SkillRanks, queue: SkillId[]): SkillRanks {
  const out = { ...ranks };
  for (const id of queue) out[id] = Math.min(MAX_RANK, out[id] + 1);
  return out;
}

/** Perks that a change from `a` to `b` unlocks, in skill order. */
function newlyUnlocked(a: SkillRanks, b: SkillRanks): { skill: SkillDef; rank: 5 | 10; perk: PerkId }[] {
  const out: { skill: SkillDef; rank: 5 | 10; perk: PerkId }[] = [];
  for (const d of SKILLS) for (const r of PERK_RANKS) if (a[d.id] < r && b[d.id] >= r) out.push({ skill: d, rank: r, perk: d.perks[r] });
  return out;
}

function usePerkText() {
  const { t } = useTranslation();
  return (perk: PerkId) => ({
    name: t(`screens.perks.${perk}.name`),
    fx: t(`screens.perks.${perk}.fx`, perkParams(perk)),
  });
}

function SkillRow({
  def,
  saved,
  rank,
  spLeft,
  onAdd,
  toast,
}: {
  def: SkillDef;
  saved: number;
  rank: number;
  spLeft: number;
  onAdd: () => void;
  toast: Toast | null;
}) {
  const { t } = useTranslation();
  const perkText = usePerkText();
  const max = rank >= MAX_RANK;
  const cost = max ? 0 : rankCost(rank + 1);
  const can = !max && cost <= spLeft;
  const pend = rank > saved;
  const name = t(`screens.skills.${def.id}.name`);
  const unit = def.display === 'health' ? ` ${t('screens.training.hp')}` : '';

  // Perk lines: unlocked ones, pending ones, then the next locked one.
  const perks: { rank: 5 | 10; perk: PerkId; st: 'on' | 'new' | 'pending' | 'next' }[] = [];
  for (const r of PERK_RANKS) {
    const perk = def.perks[r];
    if (saved >= r) perks.push({ rank: r, perk, st: toast && toast.skill === def.id && toast.rank === r ? 'new' : 'on' });
    else if (rank >= r) perks.push({ rank: r, perk, st: 'pending' });
    else if (!perks.some((p) => p.st === 'next')) perks.push({ rank: r, perk, st: 'next' });
  }

  return (
    <li className={s.skill}>
      <div className={s.skillMain}>
        <div className={s.skillBody}>
          <div className={s.skillHead}>
            <span className={s.skillName}>{name}</span>
            {/* Perk names only; what a perk does shows on hover (as on the Profile) and in the toast when it unlocks. */}
            <ul className={s.perks}>
              {perks.map((p) => {
                const txt = perkText(p.perk);
                return (
                  <li key={p.rank} className={[s.perk, s[`perk_${p.st}`]].join(' ')} title={txt.fx}>
                    <span className={s.perkBadge}>
                      {p.st === 'next' ? '☆' : '★'}
                      {p.rank}
                    </span>
                    <span className={s.perkName}>{txt.name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className={s.pips} aria-hidden="true">
            {Array.from({ length: MAX_RANK }, (_, i) => {
              const perk = i === 4 || i === 9;
              const on = i < saved;
              const pn = i >= saved && i < rank;
              const isNew = perk && toast?.skill === def.id && i === toast.rank - 1;
              return (
                <span
                  key={i}
                  className={[s.pip, on ? s.pipOn : pn ? s.pipPend : '', perk && !on && !pn ? s.pipPerk : '', isNew ? s.pipNew : ''].join(' ')}
                >
                  {perk ? '★' : ''}
                </span>
              );
            })}
          </div>
          <div className={s.effect}>
            {t(`screens.skills.${def.id}.effect`)} <span className={pend ? s.green : s.white}>{skillEffect(def, rank)}</span>
            <span className={max || !can ? s.arrowDim : s.arrow}>
              {' → '}
              {max ? t('screens.training.max') : skillEffect(def, rank + 1) + unit}
            </span>
          </div>
        </div>
        <button
          type="button"
          className={[s.add, can ? s.addOn : ''].join(' ')}
          onClick={onAdd}
          disabled={!can}
          aria-label={
            max
              ? t('screens.training.maxAria', { skill: name })
              : t('screens.training.addAria', { skill: name, rank: rank + 1, count: cost })
          }
        >
          <span className={max ? s.addMax : s.addPlus}>{max ? t('screens.training.max') : '+'}</span>
          {!max && <span className={s.addCost}>{t('screens.training.pts', { count: cost })}</span>}
        </button>
      </div>
    </li>
  );
}

export function TrainingScreen() {
  const { t } = useTranslation();
  const perkText = usePerkText();
  const layout = useLayoutMode();
  const desktop = layout === 'desktop';
  const progress = useApp((st) => st.progress);
  const name = useApp((st) => st.settings.playerName);
  const fighterId = useMyFighter();
  const myLook = useMyLook();
  const confirmTraining = useApp((st) => st.confirmTraining);
  const resetSkills = useApp((st) => st.resetSkills);
  const [steps, setSteps] = useState<SkillId[][]>([]);
  const [tab, setTab] = useState<SkillGroup>('striking');
  const [toast, setToast] = useState<Toast | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  /** Saving (on the game server, Milestone 4), and whether the last save failed. */
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const resetTitle = useId();
  useScrollTop();

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(id);
  }, [toast]);

  const saved = progress.ranks;
  const queue = useMemo(() => steps.flat(), [steps]);
  const eff = useMemo(() => applySteps(saved, queue), [saved, queue]);
  const pendCost = spentPoints(eff) - spentPoints(saved);
  const available = availablePoints(progress);
  const spLeft = available - pendCost;
  const hasPend = steps.length > 0;
  const build = buildLabel(eff);
  const lv = levelView(progress.xpTotal);
  const sp = skillBarView(progress.skillPointsEarned, progress.skillBarXp);
  const unlocking = newlyUnlocked(saved, eff);
  const spent = spentPoints(saved);
  const cost = resetCost(progress, Date.now());

  const add = (id: SkillId) => {
    setSteps((st) => [...st, [id]]);
    setToast(null);
    setFailed(false);
  };

  const preset = (p: PresetId) => {
    if (spLeft <= 0) return;
    const res = applyPreset(eff, spLeft, p);
    const step: SkillId[] = [];
    for (const id of SKILL_IDS) for (let i = eff[id]; i < res.ranks[id]; i++) step.push(id);
    if (step.length) {
      setSteps((st) => [...st, step]);
      setToast(null);
      setFailed(false);
    }
  };

  const confirm = async () => {
    if (busy) return;
    const unlocked = newlyUnlocked(saved, eff)[0];
    setBusy(true);
    const ok = await confirmTraining(eff);
    setBusy(false);
    setFailed(!ok);
    if (ok) {
      setSteps([]);
      setToast(unlocked ? { skill: unlocked.skill.id, rank: unlocked.rank } : null);
    }
  };

  const doReset = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await resetSkills();
    setBusy(false);
    setFailed(!ok);
    if (ok) {
      setSteps([]);
      setToast(null);
      setResetOpen(false);
    }
  };

  const buildChip = build === 'none' ? t('screens.builds.none') : t('screens.builds.chip', { label: t(`screens.builds.${build}`) });

  // ---- Parts ------------------------------------------------------------------------------

  const header = (
    <div className={desktop ? s.deskFighter : s.head}>
      {desktop && (
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.3),rgba(20,19,18,.95) 70%)" />
      )}
      <div className={s.headArt}>
        <FighterArt id={fighterId} height={desktop ? 150 : 120} alt="" look={myLook} />
      </div>
      <div className={s.headInfo}>
        <div className={s.headName}>{name}</div>
        <div>
          <span className={ui.chip}>{buildChip}</span>
        </div>
        <div className={s.headLevel}>
          <LevelBadge level={lv.level} size={desktop ? 38 : 34} />
          <div className={s.headLevelText}>
            <span className={s.headLevelTitle}>{t('screens.level.level', { level: lv.level })}</span>
            <span className={s.headXp}>
              {lv.maxed ? t('screens.level.max') : t('screens.level.xp', { now: formatNumber(lv.into), need: formatNumber(lv.needed) })}
            </span>
          </div>
        </div>
        <XpBar value={lv.frac} height={desktop ? 7 : 6} label={t('screens.level.title')} />
        <div className={s.headSp}>
          <SpBar value={sp.frac} height={3} label={t('screens.level.spLabel')} />
          <SkillPointLine pointsEarned={progress.skillPointsEarned} barXp={progress.skillBarXp} />
        </div>
      </div>
    </div>
  );

  const full = spLeft > 0;
  const pointsCard = (
    <div className={[s.points, full ? s.pointsFull : ''].join(' ')} aria-live="polite">
      <div className={[s.pointsNum, full ? s.pointsNumFull : ''].join(' ')}>{spLeft}</div>
      <div className={s.pointsText}>
        <span className={s.pointsTitle}>
          {full
            ? t('screens.training.toSpend', { count: spLeft })
            : hasPend
              ? t('screens.training.allAssigned')
              : t('screens.training.nextIn', { xp: formatNumber(sp.left) })}
        </span>
        <span className={s.pointsSub}>
          {hasPend ? t('screens.training.tapConfirm') : full ? t('screens.training.tapPlus') : t('screens.training.keepFighting')}
        </span>
      </div>
    </div>
  );

  const builds = (
    <div className={s.builds}>
      <div className={s.buildsHead}>
        <span className={ui.label}>{t('screens.training.suggested')}</span>
        <span className={ui.labelDim}>{spLeft > 0 ? t('screens.training.spends', { count: spLeft }) : t('screens.training.noPoints')}</span>
      </div>
      <div className={s.buildGrid}>
        {PRESET_IDS.map((p) => (
          <button
            key={p}
            type="button"
            className={[s.build, build === p ? s.buildOn : ''].join(' ')}
            onClick={() => preset(p)}
            disabled={spLeft <= 0}
          >
            <span className={s.buildName}>{t(`screens.builds.${p}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const reset = (
    <button type="button" className={s.reset} onClick={() => setResetOpen(true)} disabled={spent === 0}>
      <span className={s.resetLabel}>{t('screens.training.reset')}</span>
      <span className={[s.resetNote, cost === 0 ? s.green : ''].join(' ')}>
        {spent === 0 ? t('screens.training.nothingToReset') : cost === 0 ? t('screens.training.resetFree') : t('screens.training.resetCost', { count: cost })}
      </span>
    </button>
  );

  const toastCard = toast
    ? (() => {
        const def = SKILLS.find((d) => d.id === toast.skill)!;
        const txt = perkText(def.perks[toast.rank]);
        return (
          <div className={s.toast} role="status">
            <div className={s.toastMark} aria-hidden="true">
              <span className={s.toastShadow} />
              <span className={s.toastFace}>★</span>
            </div>
            <div className={s.toastText}>
              <span className={s.toastWhere}>
                {t('screens.training.perkUnlocked', { skill: t(`screens.skills.${def.id}.name`), rank: toast.rank })}
              </span>
              <span className={s.toastName}>{txt.name}</span>
              <span className={s.toastFx}>{txt.fx}</span>
            </div>
          </div>
        );
      })()
    : null;

  const group = (g: SkillGroup, showHeader: boolean) => (
    <section key={g} className={s.group} aria-label={t(`screens.groups.${g}`)}>
      {showHeader && (
        <div className={s.groupHead}>
          <span className={s.groupName}>{t(`screens.groups.${g}`)}</span>
        </div>
      )}
      <ul className={s.skillList}>
        {skillsInGroup(g).map((d) => (
          <SkillRow key={d.id} def={d} saved={saved[d.id]} rank={eff[d.id]} spLeft={spLeft} onAdd={() => add(d.id)} toast={toast} />
        ))}
      </ul>
    </section>
  );

  const pendNote = [
    t('screens.training.pendRanks', { count: queue.length }),
    t('screens.training.pendPts', { count: pendCost }),
    unlocking.length
      ? t('screens.training.unlocks', { perks: unlocking.map((u) => perkText(u.perk).name).join(', ') })
      : t('screens.training.notSaved'),
  ].join(' · ');

  const pendingBar = hasPend ? (
    <div className={desktop ? s.pendDesk : s.pend}>
      <div className={s.pendInner}>
        <div className={s.pendNote} role={failed ? 'alert' : undefined}>
          {failed ? t('common.saveFailed') : pendNote}
        </div>
        <div className={s.pendButtons}>
          <button
            type="button"
            className={[ui.ghost, s.pendCancel].join(' ')}
            onClick={() => {
              setSteps([]);
              setFailed(false);
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={[ui.secondary, s.pendUndo].join(' ')}
            onClick={() => {
              setSteps((st) => st.slice(0, -1));
              setFailed(false);
            }}
          >
            <span aria-hidden="true">↶</span> {t('screens.training.undo')}
          </button>
          <button type="button" className={[ui.confirm, s.pendConfirm].join(' ')} onClick={() => void confirm()} disabled={busy}>
            {t('screens.training.confirm', { count: pendCost })}
            <span aria-hidden="true">✓</span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const resetDialog = resetOpen ? (
    <Modal onClose={() => setResetOpen(false)} labelledBy={resetTitle} variant="sheet">
      <div className={s.dialog}>
        <span className={ui.label}>{t('screens.training.reset')}</span>
        <h2 id={resetTitle} className={s.dialogTitle}>
          {t('screens.training.resetTitle')}
        </h2>
        <p className={s.dialogBody}>{t('screens.training.resetBody', { count: spent })}</p>
        <p className={[s.dialogCost, cost === 0 ? s.green : ''].join(' ')}>
          {cost === 0 ? t('screens.training.resetFreeLong') : t('screens.training.resetPaid', { count: cost, coins: formatNumber(progress.coins) })}
        </p>
        {failed && (
          <p className={s.dialogBody} role="alert">
            {t('common.saveFailed')}
          </p>
        )}
        <div className={s.dialogButtons}>
          <button type="button" className={ui.ghost} onClick={() => setResetOpen(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className={ui.danger} onClick={() => void doReset()} disabled={busy || progress.coins < cost}>
            {t('screens.training.resetConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  ) : null;

  if (desktop) {
    return (
      <div className={['cg-ui', ui.screen, s.desk].join(' ')}>
        <DesktopHeader />
        <main className={[s.deskMain, hasPend ? s.withPend : ''].join(' ')}>
          <aside className={s.deskAside}>
            {header}
            {pointsCard}
            {builds}
            {reset}
          </aside>
          <section className={s.deskSkills}>
            <div className={s.deskTitleBlock}>
              <span className={ui.label}>{t('screens.nav.training')}</span>
              <h1 className={s.deskTitle}>{t('screens.training.skillsTitle')}</h1>
            </div>
            {toastCard}
            <div className={s.deskGroups}>{SKILL_GROUPS.map((g) => group(g, true))}</div>
          </section>
        </main>
        {pendingBar}
        {resetDialog}
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen, s.phone].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <TopBar title={t('screens.nav.training')} />
        {header}
        <div className={[s.stack, hasPend ? s.withPend : ''].join(' ')}>
          {pointsCard}
          {builds}
          <div className={s.tabs} role="tablist" aria-label={t('screens.training.groupsLabel')}>
            {SKILL_GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                role="tab"
                aria-selected={tab === g}
                className={[s.tab, tab === g ? s.tabOn : ''].join(' ')}
                onClick={() => setTab(g)}
              >
                {t(`screens.groups.${g}`)}
              </button>
            ))}
          </div>
          {toastCard}
          <div role="tabpanel">{group(tab, false)}</div>
          {reset}
        </div>
        {pendingBar}
      </div>
      {resetDialog}
    </div>
  );
}
